import { useEffect, useRef, useState } from 'react';
import { AbortControllerState, RetryState } from '../constants/ai/retryConfig';
import {
  createAbortControllerState,
  executeWithRetry,
  getAIErrorMessage,
} from '../utils/aiRetryUtils';
import { logger } from '../utils/logger';
import { isAbortError } from '../utils/errorGuards';
import { reportToSentry } from '../lib/sentry';
import { AIResponseResult } from '../api/ai';

/**
 * Interface for chat API functionality
 */
export interface UseAIChatReturn {
  /** Whether the AI is currently generating a response */
  isTyping: boolean;
  /** Whether a silent retry is in progress after an error */
  isSilentlyRetrying: boolean;
  /** Current retry attempt number (0-based) */
  retryAttempt: number;
  /** Function to generate an AI response */
  generateResponse: (params: AIResponseParams) => Promise<AIResponseResult | string>;
  /** Abort any in-progress requests */
  cancelRequest: () => void;
}

/**
 * Parameters for API response generation
 */
export interface AIResponseParams {
  /** Messages to send to the API */
  messages: Array<{ role: string; content: string }>;
  /** Optional model ID to use */
  model?: string;
  /** Whether to request a structured response with content and summary */
  structuredResponse?: boolean;
  /** System prompt to prepend */
  systemPrompt?: string;
  /** Optional temperature parameter */
  temperature?: number;
  /** Optional max tokens parameter */
  maxTokens?: number;
}

/**
 * Hook for AI response generation with automatic retry functionality
 */
export function useAIChat(): UseAIChatReturn {
  // UI state
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isSilentlyRetrying, setIsSilentlyRetrying] = useState<boolean>(false);
  const [retryAttempt, setRetryAttempt] = useState<number>(0);

  // Refs to track state across renders
  const isMountedRef = useRef<boolean>(true);
  const isRetryingRef = useRef<boolean>(false);

  // Create abort controller state
  const abortControllerState = useRef<AbortControllerState>(createAbortControllerState()).current;

  // Create retry state
  const retryState: RetryState = {
    isSilentlyRetrying,
    retryAttempt,
    isRetrying: isRetryingRef.current,
    isMounted: isMountedRef.current,
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      abortControllerState.abortCurrentController();
    };
  }, []);

  /**
   * Handle retry state updates
   */
  const updateRetryState = (retrying: boolean, attempt: number) => {
    if (!isMountedRef.current) return;

    isRetryingRef.current = retrying;
    setIsSilentlyRetrying(retrying && attempt > 0);
    setRetryAttempt(attempt);
  };

  /**
   * Generate an AI response with retry capability
   */
  const generateResponse = async (params: AIResponseParams): Promise<AIResponseResult | string> => {
    // Check network state first
    if (!navigator.onLine) {
      throw new Error('Device is offline');
    }

    // Set initial UI state
    setIsTyping(true);
    updateRetryState(false, 0);

    try {
      // Define the operation to perform with retries
      const operation = async (signal: AbortSignal, retryCount: number) => {
        logger.ai('Generating AI response', {
          messageCount: params.messages.length,
          model: params.model || 'default',
          retryAttempt: retryCount,
        });

        // Build the request
        const requestBody = {
          messages: params.systemPrompt
            ? [{ role: 'system', content: params.systemPrompt }, ...params.messages]
            : params.messages,
          model: params.model,
          temperature: params.temperature,
          max_tokens: params.maxTokens,
        };

        // Send the request
        const response = await fetch('/api/ai-generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
          signal: signal,
        });

        // Handle error responses
        if (!response.ok) {
          // Try to parse the error as JSON
          const responseClone = response.clone();

          try {
            const errorJson = await responseClone.json();
            const errorMessage =
              errorJson.error || `API error: Server responded with ${response.status}`;

            const openAIError = new Error(errorMessage);
            openAIError.name = 'OpenAIApiError';
            (openAIError as any).status = response.status;
            (openAIError as any).details = errorJson.details || {};

            throw openAIError;
          } catch {
            // If JSON parsing fails, fall back to text
            const errorText = await response.text();
            const openAIError = new Error(
              `API error: Server responded with ${response.status}: ${errorText}`
            );
            openAIError.name = 'OpenAIApiError';
            (openAIError as any).status = response.status;

            throw openAIError;
          }
        }

        // Parse the successful response
        const result = await response.json();

        // Log model info from the response if available
        if (result.model) {
          logger.ai('Response received from model', {
            model: result.model,
            promptTokens: result.usage?.prompt_tokens,
            completionTokens: result.usage?.completion_tokens,
            totalTokens: result.usage?.total_tokens,
          });
        }

        const rawResponse = result.choices[0]?.message?.content;

        if (!rawResponse) {
          throw new Error('Empty response from AI');
        }

        // If structured response was requested, parse it
        if (params.structuredResponse) {
          // Parse summary and content from the response
          const summaryMatch = rawResponse.match(/\[SUMMARY](.*?)\[\/SUMMARY]/s);
          const contentMatch = rawResponse.match(/\[CONTENT](.*?)\[\/CONTENT]/s);

          if (summaryMatch && contentMatch) {
            return {
              content: contentMatch[1].trim(),
              summary: summaryMatch[1].trim(),
              rawResponse,
            };
          } else {
            // If no structured format, but we wanted one
            return {
              content: rawResponse,
              summary: null,
              rawResponse,
            };
          }
        }

        // Return raw response for non-structured requests
        return rawResponse;
      };

      // Execute the operation with retry support
      return await executeWithRetry(operation, {
        retryState: {
          ...retryState,
          isMounted: isMountedRef.current,
        },
        abortControllerState,
        onRetry: retryCount => {
          // Update UI state during retries
          updateRetryState(true, retryCount);
        },
        onSuccess: () => {
          // Reset retry state on success
          updateRetryState(false, 0);
        },
        onError: (error, retryAttempts) => {
          // Log and report the error
          logger.error('Error generating AI response after retries:', error);

          reportToSentry(
            error,
            {
              type: 'ai_chat_error',
              model: params.model || 'default',
              source: error.name === 'OpenAIApiError' ? 'openai' : 'app',
              component: 'chat',
              transaction: 'chat.generateResponse',
              retryAttempts,
            },
            'error'
          );
        },
      });
    } catch (error) {
      // Handle abort errors silently
      if (isAbortError(error)) {
        logger.ai('AI request was aborted');
        throw error;
      }

      logger.error('Final error generating AI response:', error);

      // Enhance the error with user-friendly message
      if (error instanceof Error) {
        error.message = getAIErrorMessage(error);
      }

      // Rethrow for caller to handle
      throw error;
    } finally {
      // Reset UI state
      setIsTyping(false);
      updateRetryState(false, 0);
    }
  };

  /**
   * Cancel any in-progress requests
   */
  const cancelRequest = () => {
    abortControllerState.abortCurrentController();
    updateRetryState(false, 0);
    setIsTyping(false);
  };

  return {
    isTyping,
    isSilentlyRetrying,
    retryAttempt,
    generateResponse,
    cancelRequest,
  };
}
